import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../store';
import * as S from './styles';
import ApplicationStepOne from './ApplicationStepOne';
import ApplicationStepTow from './ApplicationStepTow';
import { toast } from 'react-toastify';
import { Button, CircularProgress, Backdrop } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowBack';
import { submitApplication } from '../../thunks/unifiedApplicationThunks';
import Texts from '../Texts';
import { useUpdateApplicationData } from '../../hooks/useUpdateApplicationData';
import {
  syncStepWithServer,
  updateApplicationField,
  updateIdentityField,
} from '../../Slice/unifiedFormSlice';
import Cookies from 'js-cookie';
import { useSubmitHandler } from '../../hooks/useSubmitHandler';
import SubmitButton from '../Button/SubmitButton';
import { ACTION_BLUE, ACTION_BLUE_HOVER } from '../../theme/theme';

interface ApplicationFormProps {
  publisherId: string;
  displayName: string;
}

const ApplicationForm: React.FC<ApplicationFormProps> = ({
  publisherId,
  displayName,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { error, application, identity } = useSelector(
    (state: RootState) => state.unifiedForm,
  );
  const { updateApplicationData, error: updateError } =
    useUpdateApplicationData();
  const [localPublisherId, setLocalPublisherId] = useState<string>('');
  const [localDisplayName, setLocalDisplayName] = useState<string>('');

  // استفاده از hook برای مدیریت submit
  const { isSubmitting, handleSubmit } = useSubmitHandler({
    successMessage: 'اطلاعات اپلیکیشن با موفقیت ارسال شد',
    debounceMs: 2000, // 2 ثانیه بین submit ها
  });

  useEffect(() => {
    if (application.publisher_id) {
      setLocalPublisherId(application.publisher_id);
    } else if (publisherId) {
      setLocalPublisherId(publisherId);
      dispatch(
        updateApplicationField({ field: 'publisher_id', value: publisherId }),
      );
    }

    if (identity.display_name) {
      setLocalDisplayName(identity.display_name);
    } else if (displayName) {
      setLocalDisplayName(displayName);
      dispatch(
        updateIdentityField({ field: 'display_name', value: displayName }),
      );
    }
  }, [
    dispatch,
    publisherId,
    displayName,
    application.publisher_id,
    identity.display_name,
  ]);

  const handleFormSubmit = async () => {
    // بررسی فیلدهای الزامی به ترتیب اولویت
    if (!application.fa_name || String(application.fa_name).trim() === '') {
      toast.error('لطفاً نام فارسی اپلیکیشن را وارد کنید');
      return;
    }

    if (!application.en_name || String(application.en_name).trim() === '') {
      toast.error('لطفاً نام انگلیسی اپلیکیشن را وارد کنید');
      return;
    }

    if (!application.category || String(application.category).trim() === '') {
      toast.error('لطفاً دسته‌بندی اپلیکیشن را انتخاب کنید');
      return;
    }

    if (!application.application_file_url) {
      toast.error('لطفاً فایل اپلیکیشن را انتخاب کنید');
      return;
    }

    await handleSubmit(async () => {
      const response = await dispatch(
        submitApplication({
          fileUrl: application.application_file_url || '',
          publisher_id: localPublisherId,
          fa_name: application.fa_name,
          en_name: application.en_name,
          category: application.category || '',
        }),
      ).unwrap();
      console.log('Submission response:', JSON.stringify(response, null, 2));

      const appData = response.data || response;
      console.log('Extracted appData:', JSON.stringify(response, null, 2));

      const applicationData = {
        id: String(appData.id || 'unknown'),
        name_en: appData.name_en || '',
        name_fa: appData.name_fa || appData.name || '',
        name: appData.name || '',
        category_id: appData.category_id || null,
        description: appData.description || null,
        description_fa: appData.description_fa || null,
        description_en: appData.description_en || null,
        publisher_id: localPublisherId,
        step: appData.step || appData['current-step'] || 2,
        'next-step': appData['next-step'] || 3,
      };

      updateApplicationData(applicationData);

      dispatch(
        syncStepWithServer({
          currentStep: appData['current-step'] || appData.step || 2,
          nextStep: appData['next-step'] || 3,
        }),
      );

      console.log(
        'Stored application data:',
        applicationData,
        'Redux state after update:',
        {
          application_id: applicationData.id,
          publisher_id: localPublisherId,
          current_step: appData['current-step'],
          next_step: appData['next-step'],
        },
      );

      return response;
    });
  };

  // نمایش لودینگ تمام‌صفحه با Backdrop
  return (
    <S.Container>
      <Backdrop
        sx={{ color: '#a57b2b', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isSubmitting}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {!isSubmitting && (
        <>
          {/* <S.Title>{Texts.application.titel}</S.Title>
          <S.Description>{Texts.application.subTitle}</S.Description> */}

          <ApplicationStepOne
            id={localPublisherId}
            display_name={localDisplayName}
          />

          <ApplicationStepTow
            onFileSelected={(_file, fileUrl) => {
              dispatch(
                updateApplicationField({
                  field: 'application_file_url',
                  value: fileUrl || null,
                }),
              );
            }}
          />

          <S.SubmitBox>
            <SubmitButton
              variant="contained"
              onClick={handleFormSubmit}
              startIcon={<ArrowForwardIcon />}
              isSubmitting={isSubmitting}
              loadingText="در حال ارسال..."
              sx={{
                width: '180px',
                backgroundColor: ACTION_BLUE,
                color: '#FFFFFF',
                textTransform: 'none',
                '&:hover': { backgroundColor: ACTION_BLUE_HOVER },
                '&.Mui-disabled': {
                  backgroundColor: '#BDBDBD',
                  color: '#757575',
                },
              }}
              aria-label="ارسال اپلیکیشن"
            >
              {Texts.application.submitText}
            </SubmitButton>
          </S.SubmitBox>
        </>
      )}
    </S.Container>
  );
};

export default ApplicationForm;
